import { useState } from "react";
import { FileJson, Globe, RefreshCw } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Loader,
  cn,
} from "@wa/ui";
import { DomainSummary, type T } from "./DomainSummary";
import { DomainRawDialog } from "./DomainRawDialog";
import { useDomainCheck } from "./useDomainCheck";

/**
 * Domain check straight from a row in the register list, so qualifying a
 * prospect does not cost a page navigation. Runs on open (`autoRun`) - the modal
 * exists for no other reason, so making the operator press a second button
 * would be pure friction.
 */
export function DomainCheckDialog({
  regCode,
  companyName,
  webUrl,
  open,
  onOpenChange,
  t,
}: {
  regCode: string;
  companyName: string;
  webUrl: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  t: T;
}) {
  const { state, run } = useDomainCheck(regCode, open);
  const [rawOpen, setRawOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="size-4 text-primary" /> {t("ariregister.dns.title")}
          </DialogTitle>
          <DialogDescription>
            {companyName}
            {webUrl ? ` · ${webUrl}` : ""}
          </DialogDescription>
        </DialogHeader>

        {state.kind === "busy" && (
          <div className="flex justify-center py-12">
            <Loader size="lg" />
          </div>
        )}

        {state.kind === "error" && (
          <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            {t(state.messageKey)}
          </p>
        )}

        {state.kind === "done" && (
          <>
            <DomainSummary check={state.check} t={t} />
            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setRawOpen(true)}>
                <FileJson className="size-3.5" /> {t("ariregister.dns.raw")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => run(true)}>
                <RefreshCw className={cn("size-3.5")} /> {t("ariregister.dns.recheck")}
              </Button>
            </div>
            <DomainRawDialog check={state.check} open={rawOpen} onOpenChange={setRawOpen} t={t} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
